import { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, Star, Zap, Brain, MessageSquare, X } from 'lucide-react';
import { useThemeStore } from '../../store/theme';
import PromoVideo from './PromoVideo';

// Video Modal Component
export const VideoModal = ({ isOpen, onClose, videoUrl, title }: { isOpen: boolean; onClose: () => void; videoUrl: string; title: string }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative w-full max-w-4xl bg-dark-900 rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="aspect-video">
              <iframe
                src={videoUrl}
                title={title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Floating orb component with 3D effect
const FloatingOrb = ({
  className,
  delay = 0,
  size = 'w-64 h-64',
  color = 'bg-accent-purple/20'
}: {
  className?: string;
  delay?: number;
  size?: string;
  color?: string;
}) => (
  <motion.div
    className={`absolute rounded-full blur-3xl ${size} ${color} ${className}`}
    animate={{
      y: [0, -30, 0],
      x: [0, 15, 0],
      scale: [1, 1.1, 1],
    }}
    transition={{
      duration: 8,
      repeat: Infinity,
      delay,
      ease: "easeInOut"
    }}
  />
);

// Animated particles
const Particle = ({ index }: { index: number }) => {
  const randomX = Math.random() * 100;
  const randomDelay = Math.random() * 5;
  const randomDuration = 15 + Math.random() * 10;
  const size = Math.random() * 4 + 2;

  return (
    <motion.div
      className="absolute rounded-full bg-accent-purple/30"
      style={{
        width: size,
        height: size,
        left: `${randomX}%`,
        bottom: '-10%',
      }}
      animate={{
        y: [0, -window.innerHeight * 1.2],
        opacity: [0, 1, 1, 0],
        scale: [0, 1, 1, 0.5],
      }}
      transition={{
        duration: randomDuration,
        repeat: Infinity,
        delay: randomDelay,
        ease: "linear"
      }}
    />
  );
};

// 3D Card with mouse tracking
const Card3D = ({ children }: { children: React.ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const mouseXSpring = useSpring(x, { stiffness: 500, damping: 100 });
  const mouseYSpring = useSpring(y, { stiffness: 500, damping: 100 });

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["10deg", "-10deg"]);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-10deg", "10deg"]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const xPct = mouseX / width - 0.5;
    const yPct = mouseY / height - 0.5;
    x.set(xPct);
    y.set(yPct);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
    setIsHovered(false);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      style={{
        rotateX,
        rotateY,
        transformStyle: "preserve-3d",
      }}
      className="relative"
    >
      <motion.div
        animate={{ scale: isHovered ? 1.02 : 1 }}
        transition={{ duration: 0.3 }}
        style={{ transformStyle: "preserve-3d" }}
      >
        {children}
      </motion.div>
      {/* Glow effect on hover */}
      <motion.div
        className="absolute -inset-4 bg-gradient-accent rounded-3xl -z-10"
        animate={{ opacity: isHovered ? 0.2 : 0.1 }}
        transition={{ duration: 0.3 }}
        style={{ filter: 'blur(40px)' }}
      />
    </motion.div>
  );
};

// Animated counter
const AnimatedCounter = ({ value, suffix = '' }: { value: number; suffix?: string }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = value / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [value]);

  return <span>{count.toLocaleString()}{suffix}</span>;
};

// Live typing effect
const TypeWriter = ({ texts, className = '' }: { texts: string[]; className?: string }) => {
  const [textIndex, setTextIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentText = texts[textIndex];
    const typeSpeed = isDeleting ? 30 : 80;

    if (!isDeleting && displayText === currentText) {
      setTimeout(() => setIsDeleting(true), 2000);
      return;
    }

    if (isDeleting && displayText === '') {
      setIsDeleting(false);
      setTextIndex((prev) => (prev + 1) % texts.length);
      return;
    }

    const timeout = setTimeout(() => {
      setDisplayText(prev =>
        isDeleting
          ? prev.slice(0, -1)
          : currentText.slice(0, prev.length + 1)
      );
    }, typeSpeed);

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, textIndex, texts]);

  return (
    <span className={className}>
      {displayText}
      <span className="animate-pulse">|</span>
    </span>
  );
};

// Feature cards floating around hero
const FeatureFloat = ({
  icon: Icon,
  text,
  position,
  delay,
  isDark
}: {
  icon: React.ElementType;
  text: string;
  position: string;
  delay: number;
  isDark: boolean;
}) => (
  <motion.div
    className={`absolute ${position} hidden lg:flex items-center gap-2 px-4 py-2 backdrop-blur-sm rounded-full text-sm shadow-lg ${
      isDark
        ? 'bg-dark-800/90 border border-white/10'
        : 'bg-white/90 border border-slate-200'
    }`}
    initial={{ opacity: 0, y: 20 }}
    animate={{
      opacity: 1,
      y: [0, -10, 0],
    }}
    transition={{
      opacity: { delay, duration: 0.5 },
      y: { delay: delay + 0.5, duration: 3, repeat: Infinity, ease: "easeInOut" }
    }}
  >
    <Icon className="w-4 h-4 text-accent-purple" />
    <span className={isDark ? 'text-text-secondary' : 'text-slate-600'}>{text}</span>
  </motion.div>
);

export default function Hero3D() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [showDemoVideo, setShowDemoVideo] = useState(false);

  return (
    <>
      {/* Promotional Demo Video */}
      <PromoVideo
        isOpen={showDemoVideo}
        onClose={() => setShowDemoVideo(false)}
        type="demo"
      />
    <section className="relative pt-32 lg:pt-40 pb-16 lg:pb-24 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient orbs */}
        <FloatingOrb
          className="top-1/4 left-1/4"
          color="bg-accent-purple/20"
          delay={0}
        />
        <FloatingOrb
          className="bottom-1/3 right-1/4"
          color="bg-accent-cyan/20"
          delay={2}
        />
        <FloatingOrb
          className="top-1/2 right-1/3"
          color="bg-accent-pink/10"
          size="w-48 h-48"
          delay={4}
        />

        {/* Grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />

        {/* Particles */}
        <div className="absolute inset-0">
          {Array.from({ length: 20 }).map((_, i) => (
            <Particle key={i} index={i} />
          ))}
        </div>

        {/* Radial gradient overlay */}
        <div className={`absolute inset-0 ${isDark ? 'bg-gradient-radial from-transparent via-dark-900/50 to-dark-900' : ''}`} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Floating feature badges */}
        <FeatureFloat
          icon={Zap}
          text="15+ AI Employees"
          position="top-48 left-8"
          delay={0.8}
          isDark={isDark}
        />
        <FeatureFloat
          icon={Brain}
          text="Train with your data"
          position="top-64 right-12"
          delay={1}
          isDark={isDark}
        />
        <FeatureFloat
          icon={MessageSquare}
          text="24/7 Support"
          position="bottom-32 left-16"
          delay={1.2}
          isDark={isDark}
        />

        <div className="text-center max-w-5xl mx-auto">
          {/* Announcement Badge */}
          <Link to="/register?employee=code">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              whileHover={{ scale: 1.02 }}
              className={`inline-flex items-center px-3 sm:px-4 py-2 rounded-full backdrop-blur-sm mb-8 cursor-pointer transition-all shadow-lg max-w-full ${
                isDark
                  ? 'bg-dark-800/80 border border-white/10 hover:border-accent-green/30 hover:bg-dark-800'
                  : 'bg-white/90 border border-slate-200 hover:border-accent-green/50 hover:bg-white'
              }`}
            >
              <motion.span
                className="flex h-2 w-2 mr-2 flex-shrink-0"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-accent-green opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-green"></span>
              </motion.span>
              <span className={`text-xs sm:text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                <span className="text-accent-green font-medium">New:</span> <span className="hidden xs:inline">Code AI - </span>Developer Assistant<span className="hidden sm:inline"> now available</span>
              </span>
              <ArrowRight className={`w-4 h-4 ml-1 sm:ml-2 flex-shrink-0 ${isDark ? 'text-text-muted' : 'text-slate-400'} group-hover:translate-x-1 transition-transform`} />
            </motion.div>
          </Link>

          {/* Main Headline with Typewriter */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-5xl sm:text-6xl lg:text-8xl font-bold tracking-tight mb-6"
          >
            <span className={isDark ? 'text-white' : 'text-slate-900'}>Your AI Team</span>
            <br />
            <span className="text-gradient-animate">
              <TypeWriter
                texts={["Never Sleeps", "Works 24/7", "Scales Instantly", "Costs Less"]}
              />
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className={`text-xl sm:text-2xl max-w-3xl mx-auto mb-10 leading-relaxed ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}
          >
            Hire specialized AI employees for marketing, sales, support, and more.
            They work around the clock, so you don't have to.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
          >
            <Link
              to="/register"
              className="group relative px-8 py-4 bg-gradient-accent rounded-2xl font-semibold text-white text-lg
                       transition-all duration-300 hover:shadow-glow-md hover:scale-105 active:scale-95
                       flex items-center gap-2 w-full sm:w-auto justify-center overflow-hidden"
            >
              <span className="relative z-10">Start Free Trial</span>
              <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-accent-purple to-accent-cyan opacity-0 group-hover:opacity-100 transition-opacity"
                animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
            </Link>
            <button
              onClick={() => setShowDemoVideo(true)}
              className={`group px-8 py-4 backdrop-blur-sm rounded-2xl font-semibold text-lg
                            transition-all duration-300 flex items-center gap-2 w-full sm:w-auto justify-center ${
                              isDark
                                ? 'bg-dark-800/80 border border-white/10 text-white hover:bg-dark-700 hover:border-accent-purple/30'
                                : 'bg-white border border-slate-200 text-slate-900 hover:bg-slate-50 hover:border-accent-purple/50 shadow-lg'
                            }`}>
              <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
              <span>Watch Demo</span>
            </button>
          </motion.div>

          {/* Stats & Social Proof */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-wrap items-center justify-center gap-8 lg:gap-16"
          >
            {/* User avatars & Brand logos */}
            <div className="flex items-center gap-4">
              <div className="flex -space-x-3">
                {[
                  { img: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=100&h=100&fit=crop&crop=face', name: 'Jessica' },
                  { img: 'https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=100&h=100&fit=crop&crop=face', name: 'Alex' },
                  { img: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop&crop=face', name: 'Sophie' },
                  { img: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop&crop=face', name: 'James' },
                  { img: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=100&h=100&fit=crop&crop=face', name: 'Mia' },
                ].map((user, i) => (
                  <motion.div
                    key={i}
                    className={`w-10 h-10 rounded-full border-2 overflow-hidden ${isDark ? 'border-dark-900' : 'border-white'}`}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.8 + i * 0.1 }}
                  >
                    <img
                      src={user.img}
                      alt={user.name}
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                ))}
              </div>
              <div className="text-left">
                <div className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <AnimatedCounter value={50000} suffix="+" />
                </div>
                <div className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Active Users</div>
              </div>
            </div>

            {/* Brand logos */}
            <div className="flex items-center gap-3">
              {[
                { icon: '🔷', name: 'Stripe' },
                { icon: '🟢', name: 'Spotify' },
                { icon: '🔵', name: 'Meta' },
                { icon: '🟣', name: 'Slack' },
              ].map((brand, i) => (
                <motion.div
                  key={brand.name}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    isDark
                      ? 'bg-dark-800/80 border border-white/10'
                      : 'bg-white/80 border border-slate-200 shadow-sm'
                  }`}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 1.2 + i * 0.1, type: "spring" }}
                  title={brand.name}
                >
                  <span className="text-sm">{brand.icon}</span>
                </motion.div>
              ))}
              <span className={`text-xs ml-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>& more</span>
            </div>

            {/* Rating */}
            <div className="flex items-center gap-2">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1 + i * 0.1, type: "spring" }}
                  >
                    <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  </motion.div>
                ))}
              </div>
              <span className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>4.9</span>
              <span className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>(2,785 reviews)</span>
            </div>

            {/* Tasks completed */}
            <div className="text-center">
              <div className={`font-bold text-xl ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <AnimatedCounter value={10} suffix="M+" />
              </div>
              <div className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Tasks Completed</div>
            </div>
          </motion.div>
        </div>

      </div>
    </section>
    </>
  );
}
